import { useEffect } from "react";
import { Box, Button, Group, Slider, Text } from "@mantine/core";
import { useGo } from "../components/ConsoleShell";
import {
  Cursor, EmptyState, JsonPane, MetaChips, Panel, Prompt, ScoreBar, Screen, VfToggle, LoadError,
} from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useQueryStore } from "../stores/queryStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { jsonPretty } from "../lib/vf";
import css from "./screens.module.css";

export default function Query() {
  const go = useGo();
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const loadError = useConsoleStore((s) => s.errors.namespaces);
  const loadNamespaces = useConsoleStore((s) => s.loadNamespaces);

  const {
    namespace, text, topK, hybrid, running, result, error,
    setNamespace, setText, setTopK, toggleHybrid, run,
  } = useQueryStore();

  useEffect(() => {
    if (workspaceId && namespaces.length === 0) void loadNamespaces();
  }, [workspaceId, namespaces.length, loadNamespaces]);

  // Default to the first namespace once the list arrives.
  useEffect(() => {
    if (!namespace && namespaces.length > 0) setNamespace(namespaces[0].id);
  }, [namespace, namespaces, setNamespace]);

  if (namespaces.length === 0) {
    return (
      <Screen>
        <Prompt command="vf query" />
        <LoadError message={loadError} />
        <EmptyState
          title="nothing to query yet"
          body="Create a namespace and upsert a few documents, then come back here to test semantic and hybrid search."
          action={
            <Button size="md" fz="13.5px" fw={600} onClick={() => go("/namespaces/new")}>
              ▸ create a namespace
            </Button>
          }
        />
      </Screen>
    );
  }

  const requestPreview = {
    query_text: text,
    top_k: topK,
    ...(hybrid ? { hybrid: { bm25_text: text } } : {}),
    include_content: true,
    include_metadata: true,
  };

  return (
    <Screen>
      <Prompt command="vf query" />

      <Box className={css.split2wide}>
        <Box>
          <Box p="15px 16px" style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}>
            <div className={css.fieldLabel}>// namespace</div>
            <select
              className={`${css.nativeSelect} ${css.nativeSelectAcc}`}
              style={{ marginBottom: 14 }}
              value={namespace ?? ""}
              aria-label="namespace"
              onChange={(e) => setNamespace(e.currentTarget.value)}
            >
              {namespaces.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.label}
                </option>
              ))}
            </select>

            <div className={css.fieldLabel}>// query text</div>
            <Group gap={8} align="flex-start" wrap="nowrap" mb={14}>
              <Text component="span" c="var(--vf-ok)" fz="14px" pt={8}>
                ▸
              </Text>
              <textarea
                className={css.queryTextarea}
                rows={2}
                value={text}
                aria-label="query text"
                onChange={(e) => setText(e.currentTarget.value)}
              />
            </Group>

            <Group gap={20} align="center" mb={15} wrap="nowrap">
              <Box style={{ flex: 1 }}>
                <Text fz="13px" component="div">
                  <span style={{ color: "var(--vf-dim)" }}>--top_k </span>
                  <span style={{ color: "var(--vf-acc)" }}>{topK}</span>
                </Text>
                <Slider
                  min={1}
                  max={10}
                  step={1}
                  value={topK}
                  onChange={setTopK}
                  label={null}
                  mt={6}
                  styles={{
                    bar: { background: "var(--vf-acc)" },
                    track: { background: "var(--vf-border)" },
                    thumb: { borderColor: "var(--vf-acc)", background: "var(--vf-acc)" },
                  }}
                />
              </Box>
              <VfToggle
                checked={hybrid}
                onChange={toggleHybrid}
                onLabel="--hybrid"
                offLabel="--hybrid"
                aria-label="hybrid BM25 fusion"
              />
            </Group>

            {running ? (
              <button type="button" className={css.primaryRunning} disabled>
                running
                <Cursor />
              </button>
            ) : (
              <button type="button" className={css.primaryWide} onClick={() => void run()}>
                ▸ execute query
              </button>
            )}
          </Box>

          {error ? (
            <Text mt={16} fz="12.5px" c="var(--vf-bad)">
              ✗ {error}
            </Text>
          ) : null}

          {result ? (
            <Box mt={16} className="vf-fade">
              <Text fz="12.5px" c="var(--vf-ok)" mb={10}>
                ✓ {result.hits.length} results · {result.queryTimeMs}ms
              </Text>

              {result.hits.length === 0 ? (
                <Box
                  p="26px 18px"
                  ta="center"
                  style={{ border: "1px dashed var(--vf-border)" }}
                >
                  <Text fz="12px" c="var(--vf-dim2)" lh={1.6}>
                    This namespace has no documents yet, so there is nothing to match. Upsert a few
                    from the documents screen and run the query again.
                  </Text>
                </Box>
              ) : null}

              {result.hits.map((hit, i) => (
                <div key={hit.id} className={css.hitCard}>
                  <div className={css.hitHead}>
                    <span className={css.hitRank}>[{i + 1}]</span>
                    <span className={css.hitId} style={{ flex: 1 }}>
                      {hit.id}
                    </span>
                    <span className={css.hitScore}>{hit.score.toFixed(3)}</span>
                  </div>
                  <ScoreBar score={hit.score} />
                  <p className={css.hitBody}>{hit.content}</p>
                  <MetaChips metadata={hit.metadata} />
                </div>
              ))}
            </Box>
          ) : null}
        </Box>

        <Panel accentTitle title={`REQUEST — POST /v1/namespaces/${namespace ?? ""}/query`}>
          <JsonPane value={jsonPretty(result?.request ?? requestPreview)} maxHeight={240} />
          <Box
            p="9px 14px"
            style={{
              borderTop: "1px solid var(--vf-border)",
              borderBottom: "1px solid var(--vf-border)",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "11.5px",
              color: "var(--vf-ok)",
            }}
          >
            <span>RESPONSE</span>
            <span style={{ color: "var(--vf-dim)" }}>{result?.responseStatus ?? "—"}</span>
          </Box>
          <JsonPane
            dim
            maxHeight={300}
            value={result ? jsonPretty(result.response) : "// run a query to see the response"}
          />
        </Panel>
      </Box>
    </Screen>
  );
}
